import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

interface AuthState {
  user: any | null;
  isGuest: boolean;
  initialized: boolean;
  setUser: (user: any) => void;
  signOut: () => Promise<void>;
  clearState: () => void;
  guestAccess: (password: string) => Promise<any>;
}

const clearAllStorage = () => {
  // Clear all auth-related storage
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('sb-auth-token');
  localStorage.removeItem('supabase.auth.token');
  // Clear any other Supabase storage keys
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('sb-') || key.startsWith('supabase.')) {
      localStorage.removeItem(key);
    }
  });
};

export const useAuthStore = create(
  persist<AuthState>(
    (set) => ({
      user: null,
      isGuest: false,
      initialized: false,
      setUser: (user) => set({ user, isGuest: false, initialized: true }),
      clearState: () => {
        set({ user: null, isGuest: false, initialized: true });
        clearAllStorage();
      },
      signOut: async () => {
        try {
          await supabase.auth.signOut();
          set({ user: null, isGuest: false, initialized: true });
          clearAllStorage();
          
          // Use relative path for redirection
          if (window.location.href.includes('mode=dashboard')) {
            window.location.href = `${window.location.origin}${window.location.pathname}`;
          }
        } catch (error) {
          console.error('Sign out error:', error);
        }
      },
      guestAccess: async (password: string) => {
        try {
          const data = await authService.guestAccess(password);
          set({ isGuest: true });
          return data;
        } catch (error) {
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        // Clear everything if no valid session
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session) {
            state?.clearState();
          }
        });
      },
    }
  )
);

// Handle auth state changes
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT' || !session) {
    useAuthStore.getState().clearState();
    if (window.location.href.includes('mode=dashboard')) {
      window.location.href = `${window.location.origin}${window.location.pathname}`;
    }
  } else if (session?.user) {
    useAuthStore.getState().setUser(session.user);
  }
});